/**
 * World events of the new Yanyu (เหตุการณ์ในเมือง, docs/systems/FUN_P1.md section 4): a 15-minute cycle (900 s of game time,
 * six in-game hours), an event every 5 minutes that runs 4 minutes, then 1 minute of quiet. Rules:
 * systems/worldEvents.ts. Data only: change a time, a wave or a reward here.
 */
import type { ZoneId } from './zones';

export const WORLD_EVENTS_ZONE: ZoneId = 'yanyu2';

export const WORLD_CYCLE = {
  /** Seconds of game time for the whole cycle. */
  length: 900,
  /** Seconds each event runs. */
  run: 240,
  /** Seconds of warning (the `soon` event) before an event starts. */
  warn: 30,
} as const;

export type WorldEventId = 'fair' | 'raid' | 'storm';

/** The three events, in cycle order: event k starts at k * 300 s of the cycle. */
export const WORLD_EVENT_ORDER: readonly WorldEventId[] = ['fair', 'raid', 'storm'];
export const WORLD_EVENT_STEP = 300;

/** Names, and the modes each offers (the mode picker, systems/modes.ts: npc plays offline, pvp falls back to npc). */
export const WORLD_EVENT_INFO: Readonly<Record<WorldEventId, { readonly name: string; readonly modes: readonly ('npc' | 'pvp')[] }>> = {
  fair: { name: 'ตลาดนัดสองฝั่งน้ำ', modes: ['npc', 'pvp'] },
  raid: { name: 'โจรเนินใต้บุกเมือง', modes: ['npc', 'pvp'] },
  storm: { name: 'พายุซัดปากอ่าว', modes: ['npc', 'pvp'] },
};

/** The fair: stand in the market on either bank. Joining pays once per fair. */
export const FAIR = {
  /** Market banks: x0, x1, y0, y1. */
  areas: [
    [340, 360, 180, 420],
    [444, 464, 204, 420],
  ] as const,
  joinCultivation: 20,
  /** ชิงซาลาเปาบนหอไผ่: the bamboo tower in the west market, 4 x 4 m and 14 m tall (data/layouts/yanyu2.ts). */
  tower: {
    x: 350,
    y: 340,
    /** Buns hang on rings round the tower: a ring's height above the sea (m), the points each bun is worth. */
    rings: [
      { h: 5, points: 1 },
      { h: 9, points: 2 },
      { h: 14, points: 4 },
    ],
    /** Buns on a ring, their distance from the tower's middle (m), and how near and how level to take one. */
    buns: 12,
    radius: 2.8,
    reach: 0.9,
    band: 1.5,
    /** ตำลึง a point pays. */
    goldPerPoint: 3,
  },
  /** Fair prices: the market stalls ask this share of the list price while the fair runs (a sink: ECONOMY.md). */
  priceMult: 0.8,
  /** The top scorer: points to reach against the NPC climbers (of the tower's 84), the purse, and the title. */
  top: { points: 40, gold: 60, title: 'bun-tower-king' },
  /** ร่อนโคมลอย: sky lanterns drift east over the river; touching one in the air pays. */
  lanterns: {
    count: 8,
    /** Start x (m) of the first lantern, the drift (m/s) and the width of the loop they drift over before they wrap. */
    x0: 336,
    speed: 0.5,
    span: 130,
    /** Lantern k hangs at y = y0 + k * dy, at a height of h0 + (k mod 5) * dh (15 to 25 m). */
    y0: 200,
    dy: 30,
    h0: 15,
    dh: 2.5,
    /** How near to touch one (m, flat and in height). */
    reach: 2,
    band: 1.5,
    gold: 5,
  },
};

/** Where lantern k floats `t` seconds into the fair (town metres): a pure function of the clock. */
export const lanternAt = (k: number, t: number): { x: number; y: number; h: number } => {
  const L = FAIR.lanterns;
  return { x: L.x0 + ((t * L.speed + k * 17) % L.span), y: L.y0 + k * L.dy, h: L.h0 + (k % 5) * L.dh };
};

/** The market stalls' price multiplier at a game time (the fair is on in `yanyu2`): 1 outside the fair. */
export const fairPriceAt = (zone: ZoneId, time: number): number =>
  zone === WORLD_EVENTS_ZONE && eventAt(time % WORLD_CYCLE.length)?.id === 'fair' ? FAIR.priceMult : 1;

/** The raid (docs/systems/FUN_P1.md 4.1). Points are map metres. */
export const RAID = {
  /** Where the bandits come down from: the bandit hill ramp. */
  from: [240, 497] as const,
  /** The two targets and their strength. */
  targets: [
    { name: 'บ้านใต้ลาน', x: 300, y: 420 },
    { name: 'แผงท่าเรือตะวันตก', x: 300, y: 520 },
  ] as const,
  targetHp: 600,
  /** Health a bandit within `reach` metres of a target takes off it each second. */
  drain: 10,
  reach: 4,
  waves: [
    { at: 0, bandits: 6, leader: false },
    { at: 80, bandits: 10, leader: false },
    { at: 160, bandits: 4, leader: true },
  ] as const,
  /** The leader is the bandit def with this many times the health. */
  leaderHpMul: 6,
  leaderName: 'หัวหน้าโจรเนินใต้',
  /** Score: a defeat, the leader. */
  score: { kill: 5, leader: 20 },
  /** Tiers by score: [least score, ตำลึง, แต้มฝึกปรือ]. */
  tiers: [
    [90, 90, 120],
    [40, 60, 80],
    [10, 30, 40],
  ] as const,
  /** Winning (the leader falls, or a target stands at the end) multiplies the pay. */
  winMul: 1.25,
  /** สู้กับ NPC: four town watchmen defend with the player, so the bandits drain the targets at this share. */
  watchmenMul: 0.6,
  /** Seconds a killed raider's body stays before it is cleared (it never respawns during the event). */
  corpse: 3,
};

/**
 * The storm (docs/systems/FUN_P1.md 4.3): the weather is a storm for the event and the sea's surge doubles (systems/water.ts,
 * `stormSurge`). Six fishermen are stranded; standing within `reach` of one for `hold` seconds rescues them (the
 * carry to a pier is left out: the rescue is the hold). Each rescue pays; all six pay a bonus. Each stranded person is
 * lost after `breath` seconds. Without water-skim the three rope posts on the west piers haul a boat in (data only
 * for now: `ropes`).
 */
export const STORM = {
  weather: 'storm' as const,
  surgeMul: 2,
  stranded: [
    { name: 'เรือล่มกลางทะเล', x: 290, y: 585 },
    { name: 'เรือล่มกลางทะเล', x: 420, y: 592 },
    { name: 'เรือล่มกลางทะเล', x: 540, y: 580 },
    { name: 'เสาปากแม่น้ำ', x: 340, y: 548 },
    { name: 'เสาปากแม่น้ำ', x: 372, y: 540 },
    { name: 'เสาปากแม่น้ำ', x: 452, y: 545 },
  ] as const,
  /** Pier ends the rescued are brought to (kept as data for the carry). */
  piers: [
    [283, 590],
    [329, 594],
    [503, 590],
  ] as const,
  ropes: [
    [283, 560],
    [305, 560],
    [329, 560],
  ] as const,
  reach: 2,
  hold: 2,
  breath: 120,
  gold: 25,
  cultivation: 30,
  allBonusGold: 50,
  /** สู้กับ NPC: the harbour's boat crew rescue one person per this many seconds, so slow play loses rescues. */
  crewEvery: 50,
};

/** Whether the storm of the cycle is on at game time `t`. */
export const stormAt = (t: number): boolean => eventAt(t % WORLD_CYCLE.length)?.id === 'storm';

/** The event running at a time of the cycle (seconds into it), or null in the quiet minute. */
export function eventAt(phase: number): { id: WorldEventId; t: number } | null {
  const k = Math.floor(phase / WORLD_EVENT_STEP);
  const t = phase - k * WORLD_EVENT_STEP;
  return k < WORLD_EVENT_ORDER.length && t < WORLD_CYCLE.run ? { id: WORLD_EVENT_ORDER[k]!, t } : null;
}

/** Seconds until the next event starts, from a time of the cycle, and which. */
export function nextEvent(phase: number): { id: WorldEventId; in: number } {
  const k = Math.floor(phase / WORLD_EVENT_STEP);
  const nextStart = (k + 1) * WORLD_EVENT_STEP;
  const here = eventAt(phase);
  if (!here && k < WORLD_EVENT_ORDER.length && phase < k * WORLD_EVENT_STEP) return { id: WORLD_EVENT_ORDER[k]!, in: k * WORLD_EVENT_STEP - phase };
  if (nextStart >= WORLD_CYCLE.length) return { id: WORLD_EVENT_ORDER[0]!, in: WORLD_CYCLE.length - phase };
  return { id: WORLD_EVENT_ORDER[k + 1]!, in: nextStart - phase };
}
